import { ArrowLeft, Check, Plus, Save, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button, Field, InlineMessage, PageHeader } from "../../components/ui";
import { useApp } from "../../domain/AppContext";
import { compareClassNames, formatClassName } from "../../domain/classCode";
import { olympiadAreas } from "../../domain/olympiadAreas";
import {
  formatPhaseDate,
  getPhaseDateRange,
  isPaidOlympiad,
  isPhaseWindow,
  validatePhaseContinuity,
} from "../../domain/rules";
import type { Olympiad, OlympiadPhase } from "../../domain/types";

const makePhase = (index: number, date = "2026-10-15"): OlympiadPhase => ({
  id: crypto.randomUUID(),
  title: `Fase ${index + 1}`,
  date,
  dateType: "date",
  endDate: "",
  datesVaryByClass: false,
  classDates: {},
  time: "09:00",
  location: "",
  status: index ? "planned" : "next",
  notes: "",
  feeAmount: 0,
});
const initialOlympiad: Olympiad = {
  id: "",
  slug: "",
  acronym: "",
  name: "",
  area: "Ciências",
  description: "",
  status: "upcoming",
  enrollmentStart: "2026-09-01",
  enrollmentEnd: "2026-09-30",
  enrollmentEnabled: true,
  enrollmentMode: "system",
  enrollmentFormUrl: "",
  continuedEnrollmentEnabled: false,
  continuedEnrollmentUrl: "",
  examDate: "2026-10-15",
  examTime: "09:00",
  examLocation: "",
  modality: "Presencial",
  format: "Solo",
  variableTeamSize: false,
  teamMin: 2,
  teamMax: 2,
  cost: 0,
  costType: "free",
  studentFeeEnabled: false,
  schoolFeeEnabled: false,
  schoolFeeAmount: 0,
  feeVariationByPhase: false,
  feeVariationByClass: false,
  classFees: {},
  classPhaseFees: {},
  eligibleGrades: [],
  eligibleClasses: [],
  registrations: 0,
  allowed: true,
  published: false,
  content: "",
  additionalInfo: "",
  phases: [makePhase(0)],
};
const sections = [
  { id: "identification", label: "Informações básicas", hint: "Identidade e área" },
  { id: "audience", label: "Participação e turmas", hint: "Quem pode participar" },
  { id: "enrollment", label: "Inscrições", hint: "Prazo e continuidade" },
  { id: "phases", label: "Provas e fases", hint: "Datas e sequência" },
  { id: "billing", label: "Cobrança", hint: "Valores aplicáveis" },
  { id: "content", label: "Conteúdo", hint: "Orientações finais" },
  { id: "review", label: "Revisão", hint: "Conferência do rascunho" },
] as const;
type SectionId = (typeof sections)[number]["id"];
type Setter = <K extends keyof Olympiad>(key: K, value: Olympiad[K]) => void;

const validUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
};

function validateSection(form: Olympiad, section: SectionId): string | null {
  if (section === "identification") {
    if (!form.acronym.trim()) return "Informe a sigla da olimpíada.";
    if (!form.name.trim()) return "Informe o nome da olimpíada.";
    if (!form.area) return "Selecione a área da olimpíada.";
  }
  if (section === "audience") {
    if (!form.eligibleClasses?.length)
      return "Selecione ao menos uma turma participante.";
    if (form.format === "Equipe") {
      const minimum = form.teamMin ?? 0;
      const maximum = form.variableTeamSize ? form.teamMax ?? 0 : minimum;
      if (minimum < 2) return "Equipes precisam ter ao menos 2 integrantes.";
      if (maximum < minimum)
        return "O máximo de integrantes não pode ser menor que o mínimo.";
    }
  }
  if (section === "enrollment") {
    if (!form.enrollmentStart || !form.enrollmentEnd)
      return "Informe o início e o encerramento das inscrições.";
    if (form.enrollmentEnd < form.enrollmentStart)
      return "O encerramento das inscrições deve ser posterior ao início.";
    if (
      form.continuedEnrollmentEnabled &&
      !validUrl(form.continuedEnrollmentUrl?.trim() ?? "")
    )
      return "Informe uma URL válida para a etapa externa da inscrição continuada.";
  }
  if (section === "phases") {
    if (!form.phases.length) return "Cadastre ao menos uma fase.";
    return (
      validatePhaseContinuity(form.phases, form.eligibleClasses ?? [])[0] ?? null
    );
  }
  if (section === "billing" && form.costType === "paid") {
    if (!form.studentFeeEnabled && !form.schoolFeeEnabled)
      return "Defina quem assume a cobrança ou marque a olimpíada como gratuita.";
    if (form.schoolFeeEnabled && (form.schoolFeeAmount ?? 0) <= 0)
      return "Informe o valor assumido pelo colégio.";
    if (form.studentFeeEnabled) {
      if (!form.feeVariationByClass && !form.feeVariationByPhase && form.cost <= 0)
        return "Informe o valor cobrado dos estudantes.";
      if (form.feeVariationByPhase && !form.feeVariationByClass && form.phases.some((phase) => (phase.feeAmount ?? 0) <= 0))
        return "Informe um valor maior que zero para cada fase.";
      if (form.feeVariationByClass && !form.feeVariationByPhase && (form.eligibleClasses ?? []).some((className) => (form.classFees?.[className] ?? 0) <= 0))
        return "Informe um valor maior que zero para cada turma.";
      if (form.feeVariationByClass && form.feeVariationByPhase && (form.eligibleClasses ?? []).some((className) => form.phases.some((phase) => (form.classPhaseFees?.[className]?.[phase.id] ?? 0) <= 0)))
        return "Preencha todos os valores da matriz de turmas e fases.";
    }
  }
  return null;
}

export function NewOlympiadPage() {
  const { profile, data, saveOlympiad, notify } = useApp();
  const navigate = useNavigate();
  const { slug: editingSlug } = useParams();
  const [searchParams] = useSearchParams();
  const duplicateSlug = searchParams.get("duplicar");
  const initialized = useRef(false);
  const [form, setForm] = useState<Olympiad>(() =>
    structuredClone(initialOlympiad),
  );
  const [section, setSection] = useState(0);
  const [furthestSection, setFurthestSection] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const editing = Boolean(editingSlug);
  const set: Setter = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));
  const classes = useMemo(
    () =>
      data
        ? [...new Set(data.students.map((item) => item.className))]
            .filter((item) => item !== "Sem turma")
            .sort(compareClassNames)
        : [],
    [data],
  );
  useEffect(() => {
    if (!data || initialized.current) return;
    const source = data.olympiads.find((item) => item.slug === (editingSlug || duplicateSlug));
    if (!source) return;
    initialized.current = true;
    const normalizedSource = {
      ...structuredClone(source),
      enrollmentMode: "system" as const,
      enrollmentFormUrl: "",
      studentFeeEnabled: source.studentFeeEnabled ?? isPaidOlympiad(source),
      phases: source.phases.map((phase) => ({
        ...phase,
        dateType: isPhaseWindow(phase) ? ("window" as const) : ("date" as const),
        endDate: phase.endDate ?? "",
        datesVaryByClass: phase.datesVaryByClass === true,
        classDates: phase.classDates ?? {},
      })),
    };
    setFurthestSection(sections.length - 1);
    if (editingSlug) setForm(normalizedSource);
    else {
      const phaseIds = new Map(source.phases.map((phase) => [phase.id, crypto.randomUUID()]));
      const classPhaseFees = Object.fromEntries(Object.entries(source.classPhaseFees ?? {}).map(([className, fees]) => [className, Object.fromEntries(Object.entries(fees).map(([phaseId, amount]) => [phaseIds.get(phaseId) ?? phaseId, amount]))]));
      setForm({ ...normalizedSource, id: '', slug: '', acronym: `${source.acronym} CÓPIA`, name: `${source.name} (cópia)`, registrations: 0, published: false, phases: normalizedSource.phases.map((phase) => ({ ...phase, id: phaseIds.get(phase.id)! })), classPhaseFees });
    }
  }, [data, duplicateSlug, editingSlug]);
  if (!data) return null;

  const updatePhase = (id: string, patch: Partial<OlympiadPhase>) =>
    set(
      "phases",
      form.phases.map((item) =>
        item.id === id ? { ...item, ...patch } : item,
      ),
    );
  const toggleClass = (className: string) =>
    setForm((current) => {
      const removing = current.eligibleClasses?.includes(className) === true;
      const eligibleClasses = removing
        ? current.eligibleClasses?.filter((item) => item !== className) ?? []
        : [...(current.eligibleClasses ?? []), className];
      const classFees = { ...current.classFees };
      const classPhaseFees = { ...current.classPhaseFees };
      if (removing) {
        delete classFees[className];
        delete classPhaseFees[className];
      }
      return {
        ...current,
        eligibleClasses,
        classFees,
        classPhaseFees,
        phases: current.phases.map((phase) => {
          if (!phase.datesVaryByClass) return phase;
          const classDates = { ...phase.classDates };
          if (removing) delete classDates[className];
          else classDates[className] = {
            date: phase.date,
            endDate: isPhaseWindow(phase) ? phase.endDate || phase.date : "",
          };
          return { ...phase, classDates };
        }),
      };
    });
  const goToSection = (index: number) => {
    if (index > furthestSection) return;
    setError("");
    setSection(index);
  };
  const continueToNextSection = () => {
    const currentError = validateSection(form, sections[section].id);
    if (currentError) return setError(currentError);
    const nextSection = Math.min(section + 1, sections.length - 1);
    setError("");
    setFurthestSection((value) => Math.max(value, nextSection));
    setSection(nextSection);
  };
  const submit = async () => {
    setError("");
    const invalidSection = sections.findIndex((item) => validateSection(form, item.id));
    if (invalidSection >= 0) {
      setSection(invalidSection);
      setFurthestSection((value) => Math.max(value, invalidSection));
      setError(validateSection(form, sections[invalidSection].id) ?? "Revise os dados informados.");
      return;
    }
    setSaving(true);
    const acronym = form.acronym.trim().toUpperCase();
    const eligibleGrades = [
      ...new Set(
        data.students
          .filter((student) =>
            form.eligibleClasses?.includes(student.className),
          )
          .map((student) => student.grade),
      ),
    ];
    const firstPhaseDate = getPhaseDateRange(
      form.phases[0],
      form.phases[0]?.datesVaryByClass ? form.eligibleClasses?.[0] : undefined,
    ).date;
    const editionYear = Number(firstPhaseDate.slice(0, 4)) || new Date().getFullYear();
    const today = new Date().toLocaleDateString("sv-SE");
    const record: Olympiad = {
      ...form,
      id: form.id || crypto.randomUUID(),
      unitId: form.unitId || profile?.unitId,
      acronym,
      eligibleGrades,
      slug: `${acronym.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("pt-BR").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}-${editionYear}`,
      status:
        form.enrollmentEnabled === false || form.enrollmentEnd < today
          ? "closed"
          : form.enrollmentStart > today
            ? "upcoming"
            : "open",
      allowed: form.enrollmentEnabled !== false,
      cost: form.costType === "paid" ? form.cost : 0,
      studentFeeEnabled: form.costType === "paid" && form.studentFeeEnabled,
      schoolFeeEnabled: form.costType === "paid" && form.schoolFeeEnabled,
      schoolFeeAmount:
        form.costType === "paid" && form.schoolFeeEnabled
          ? form.schoolFeeAmount
          : 0,
      feeVariationByPhase: form.costType === "paid" && form.feeVariationByPhase,
      feeVariationByClass: form.costType === "paid" && form.feeVariationByClass,
      enrollmentMode: "system",
      enrollmentFormUrl: "",
      continuedEnrollmentEnabled:
        form.continuedEnrollmentEnabled,
      continuedEnrollmentUrl:
        form.continuedEnrollmentEnabled
          ? form.continuedEnrollmentUrl
          : "",
      phases: form.phases.map((phase) => ({
        ...phase,
        dateType: isPhaseWindow(phase) ? "window" : "date",
        endDate: isPhaseWindow(phase) ? phase.endDate : "",
        classDates: phase.datesVaryByClass
          ? Object.fromEntries(
              (form.eligibleClasses ?? []).map((className) => [
                className,
                phase.classDates?.[className] ?? { date: "", endDate: "" },
              ]),
            )
          : {},
      })),
      examDate: firstPhaseDate || form.examDate,
      examTime: form.phases[0]?.time || form.examTime,
      examLocation: form.phases[0]?.location || form.examLocation,
    };
    const result = await saveOlympiad(record);
    setSaving(false);
    if (result.ok) {
      notify({
        type: "success",
        title: editing ? "Alterações salvas" : "Olimpíada salva como rascunho",
        description: "Turmas, cobrança e fases foram preservadas.",
      });
      navigate(`/olimpiadas/${result.data.slug}`);
    } else setError(result.error.message);
  };

  const paid = isPaidOlympiad(form);
  return (
    <div className="new-olympiad-page">
      <button
        type="button"
        className="back-link"
        onClick={() => navigate("/olimpiadas")}
      >
        <ArrowLeft size={17} /> Voltar para olimpíadas
      </button>
      <PageHeader
        eyebrow="Cadastro completo e seguro"
        title={editing ? `Editar ${form.acronym || 'olimpíada'}` : duplicateSlug ? 'Duplicar olimpíada' : 'Nova olimpíada'}
        description={editing ? 'Revise a configuração existente com as mesmas validações do cadastro.' : 'Configure participação, turmas, fases e cobrança antes de publicar.'}
        actions={
          <Button loading={saving} onClick={submit}>
            <Save size={17} /> {editing ? 'Salvar alterações' : 'Salvar rascunho'}
          </Button>
        }
      />
      <div className="olympiad-form-layout">
        <aside>
          <ol>
            {sections.map((item, index) => (
              <li
                key={item.id}
                className={
                  section === index ? "active" : index < furthestSection ? "done" : ""
                }
              >
                <button
                  type="button"
                  disabled={index > furthestSection}
                  aria-current={section === index ? "step" : undefined}
                  onClick={() => goToSection(index)}
                >
                  <span>
                    {index < furthestSection ? <Check size={14} /> : index + 1}
                  </span>
                  <span className="step-copy">
                    <strong>{item.label}</strong>
                    <small>{item.hint}</small>
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </aside>
        <main className="olympiad-form-section">
          {section === 0 ? (
            <Identification form={form} set={set} />
          ) : null}
          {section === 1 ? (
            <AudienceAndFormat
              form={form}
              set={set}
              classes={classes}
              toggleClass={toggleClass}
            />
          ) : null}
          {section === 2 ? <EnrollmentSettings form={form} set={set} /> : null}
          {section === 3 ? (
            <Phases
              form={form}
              set={set}
              update={updatePhase}
              classes={[...(form.eligibleClasses ?? [])].sort(compareClassNames)}
              showFee={
                paid &&
                form.feeVariationByPhase === true &&
                form.feeVariationByClass !== true
              }
            />
          ) : null}
          {section === 4 ? (
            <Billing form={form} set={set} classes={classes} />
          ) : null}
          {section === 5 ? (
            <ContentSettings form={form} set={set} />
          ) : null}
          {section === 6 ? <Review form={form} /> : null}
          {error ? <InlineMessage type="error" title={error} /> : null}
          <footer className="form-section-actions">
            <Button
              variant="ghost"
              disabled={!section}
              onClick={() => goToSection(section - 1)}
            >
              Voltar
            </Button>
            {section < 6 ? (
              <Button onClick={continueToNextSection}>
                Salvar e continuar
              </Button>
            ) : (
              <Button loading={saving} onClick={submit}>
                Salvar rascunho
              </Button>
            )}
          </footer>
        </main>
      </div>
    </div>
  );
}

function Identification({ form, set }: { form: Olympiad; set: Setter }) {
  return (
    <>
      <div className="form-section-intro">
        <p className="eyebrow">Etapa 1 de 7</p>
        <h2>Informações básicas</h2>
        <p>Defina como a competição será identificada em toda a plataforma.</p>
      </div>
      <div className="form-grid two">
        <Field label="Sigla">
          <input
            value={form.acronym}
            maxLength={12}
            required
            placeholder="Ex.: OBA"
            onChange={(e) => set("acronym", e.target.value)}
          />
        </Field>
        <Field label="Nome da olimpíada">
          <input
            value={form.name}
            required
            placeholder="Nome oficial da competição"
            onChange={(e) => set("name", e.target.value)}
          />
        </Field>
        <Field label="Área">
          <select
            value={form.area}
            onChange={(e) => set("area", e.target.value)}
          >
            {olympiadAreas.map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Descrição" hint="Resumo breve exibido nos cards e na visão geral.">
        <textarea
          rows={4}
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
          placeholder="Apresente o objetivo e o tema da olimpíada."
        />
      </Field>
    </>
  );
}

function Phases({
  form,
  set,
  update,
  classes,
  showFee,
}: {
  form: Olympiad;
  set: Setter;
  update: (id: string, patch: Partial<OlympiadPhase>) => void;
  classes: string[];
  showFee: boolean;
}) {
  const add = () => {
    if (form.phases.length >= 20) return;
    const previous = form.phases.at(-1);
    const nextDate = previous
      ? getPhaseDateRange(previous, previous.datesVaryByClass ? classes[0] : undefined).endDate || previous.date
      : undefined;
    set("phases", [...form.phases, makePhase(form.phases.length, nextDate)]);
  };
  const remove = (id: string) => {
    if (form.phases.length <= 1) return;
    set("phases", form.phases.filter((item) => item.id !== id));
    set(
      "classPhaseFees",
      Object.fromEntries(
        Object.entries(form.classPhaseFees ?? {}).map(([className, values]) => [
          className,
          Object.fromEntries(Object.entries(values).filter(([phaseId]) => phaseId !== id)),
        ]),
      ),
    );
  };
  const setDateType = (phase: OlympiadPhase, dateType: "date" | "window") =>
    update(phase.id, {
      dateType,
      endDate: dateType === "window" ? phase.endDate || phase.date : "",
      classDates: Object.fromEntries(
        Object.entries(phase.classDates ?? {}).map(([className, dates]) => [
          className,
          {
            date: dates.date,
            endDate: dateType === "window" ? dates.endDate || dates.date : "",
          },
        ]),
      ),
    });
  const setDatesVaryByClass = (phase: OlympiadPhase, enabled: boolean) =>
    update(phase.id, {
      datesVaryByClass: enabled,
      classDates: enabled
        ? Object.fromEntries(
            classes.map((className) => [
              className,
              phase.classDates?.[className] ?? {
                date: phase.date,
                endDate: isPhaseWindow(phase) ? phase.endDate || phase.date : "",
              },
            ]),
          )
        : phase.classDates,
    });
  const updateClassDate = (
    phase: OlympiadPhase,
    className: string,
    patch: { date?: string; endDate?: string },
  ) =>
    update(phase.id, {
      classDates: {
        ...phase.classDates,
        [className]: { date: "", ...phase.classDates?.[className], ...patch },
      },
    });
  return (
    <>
      <div className="section-heading">
        <div>
          <p className="eyebrow">Etapa 4 de 7</p>
          <h2>Provas e fases</h2>
          <p>Organize até 20 etapas em sequência, com uma data ou uma janela de aplicação.</p>
        </div>
        <Button
          variant="secondary"
          onClick={add}
          disabled={form.phases.length >= 20}
        >
          <Plus size={17} /> Adicionar fase
        </Button>
      </div>
      <div className="phase-editor-list">
        {form.phases.map((phase, index) => (
          <fieldset key={phase.id}>
            <legend>Fase {index + 1}</legend>
            <div className="phase-editor-heading">
              <Field label="Nome">
                <input
                  value={phase.title}
                  onChange={(e) => update(phase.id, { title: e.target.value })}
                />
              </Field>
              {form.phases.length > 1 ? (
                <Button
                  variant="ghost"
                  aria-label={`Remover fase ${index + 1}`}
                  onClick={() => remove(phase.id)}
                >
                  <Trash2 size={16} />
                </Button>
              ) : null}
            </div>
            <div className="phase-configuration">
              <div>
                <span className="field-title">Período da prova</span>
                <div className="binary-options" role="group" aria-label={`Período da ${phase.title}`}>
                  <button type="button" className={!isPhaseWindow(phase) ? "active" : ""} aria-pressed={!isPhaseWindow(phase)} onClick={() => setDateType(phase, "date")}>Data única</button>
                  <button type="button" className={isPhaseWindow(phase) ? "active" : ""} aria-pressed={isPhaseWindow(phase)} onClick={() => setDateType(phase, "window")}>Janela de datas</button>
                </div>
              </div>
              <Toggle
                title="Datas diferentes por turma"
                description="Cada turma participante terá sua própria data ou janela."
                checked={phase.datesVaryByClass === true}
                onChange={(value) => setDatesVaryByClass(phase, value)}
              />
            </div>
            {phase.datesVaryByClass ? (
              <div className="class-date-list">
                <div className="subsection-heading">
                  <strong>Calendário das turmas</strong>
                  <span>{classes.length} {classes.length === 1 ? "turma vinculada" : "turmas vinculadas"}</span>
                </div>
                {classes.length ? classes.map((className) => (
                  <section key={className}>
                    <strong>{formatClassName(className)}</strong>
                    <div className={isPhaseWindow(phase) ? "form-grid two" : "form-grid"}>
                      <Field label={isPhaseWindow(phase) ? "Data inicial" : "Data da prova"}>
                        <input type="date" required value={phase.classDates?.[className]?.date ?? ""} onChange={(event) => updateClassDate(phase, className, { date: event.target.value })} />
                      </Field>
                      {isPhaseWindow(phase) ? (
                        <Field label="Data final">
                          <input type="date" required min={phase.classDates?.[className]?.date ?? ""} value={phase.classDates?.[className]?.endDate ?? ""} onChange={(event) => updateClassDate(phase, className, { endDate: event.target.value })} />
                        </Field>
                      ) : null}
                    </div>
                  </section>
                )) : (
                  <InlineMessage type="error" title="Nenhuma turma vinculada">
                    Volte à etapa “Participação e turmas” e selecione quem fará a prova.
                  </InlineMessage>
                )}
              </div>
            ) : (
              <div className={isPhaseWindow(phase) ? "form-grid two" : "form-grid"}>
                <Field label={isPhaseWindow(phase) ? "Data inicial" : "Data da prova"}>
                  <input type="date" required value={phase.date} onChange={(event) => update(phase.id, { date: event.target.value })} />
                </Field>
                {isPhaseWindow(phase) ? (
                  <Field label="Data final">
                    <input type="date" required min={phase.date} value={phase.endDate ?? ""} onChange={(event) => update(phase.id, { endDate: event.target.value })} />
                  </Field>
                ) : null}
              </div>
            )}
            <div className={`form-grid ${showFee ? "three" : "two"}`}>
              <Field label="Horário">
                <input
                  type="time"
                  value={phase.time ?? ""}
                  onChange={(e) => update(phase.id, { time: e.target.value })}
                />
              </Field>
              <Field label="Local">
                <input
                  value={phase.location ?? ""}
                  onChange={(e) =>
                    update(phase.id, { location: e.target.value })
                  }
                />
              </Field>
              {showFee ? (
                <Field label="Taxa da fase">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={phase.feeAmount ?? 0}
                    onChange={(e) =>
                      update(phase.id, { feeAmount: Number(e.target.value) })
                    }
                  />
                </Field>
              ) : null}
            </div>
            <Field label="Informações adicionais">
              <textarea
                rows={2}
                value={phase.notes ?? ""}
                onChange={(e) => update(phase.id, { notes: e.target.value })}
              />
            </Field>
          </fieldset>
        ))}
      </div>
    </>
  );
}

function EnrollmentSettings({ form, set }: { form: Olympiad; set: Setter }) {
  return (
    <>
      <div className="form-section-intro">
        <p className="eyebrow">Etapa 3 de 7</p>
        <h2>Inscrições</h2>
        <p>Toda inscrição começa e permanece registrada no Oficina Sapiens.</p>
      </div>
      <InlineMessage type="success" title="Fluxo pelo Oficina Sapiens">
        A plataforma valida elegibilidade, participantes e cobrança antes de confirmar a inscrição.
      </InlineMessage>
      <div className="form-grid two">
        <Field label="Início">
          <input
            type="date"
            value={form.enrollmentStart}
            onChange={(e) => set("enrollmentStart", e.target.value)}
          />
        </Field>
        <Field label="Encerramento">
          <input
            type="date"
            value={form.enrollmentEnd}
            onChange={(e) => set("enrollmentEnd", e.target.value)}
          />
        </Field>
      </div>
      <Toggle
        title="Inscrições habilitadas"
        description="Pode ser desligado independentemente das datas."
        checked={form.enrollmentEnabled !== false}
        onChange={(value) => set("enrollmentEnabled", value)}
      />
      <Toggle
        title="Inscrição continuada"
        description="Após o registro no Oficina Sapiens, exige uma etapa complementar em site externo."
        checked={form.continuedEnrollmentEnabled === true}
        onChange={(value) => set("continuedEnrollmentEnabled", value)}
      />
      {form.continuedEnrollmentEnabled ? (
        <Field label="URL da etapa externa" hint="O link será apresentado depois que a inscrição local for registrada.">
          <input
            type="url"
            required
            value={form.continuedEnrollmentUrl ?? ""}
            onChange={(e) => set("continuedEnrollmentUrl", e.target.value)}
            placeholder="https://"
          />
        </Field>
      ) : null}
    </>
  );
}

function Billing({
  form,
  set,
  classes,
}: {
  form: Olympiad;
  set: Setter;
  classes: string[];
}) {
  const paid = form.costType === "paid";
  const selected = form.eligibleClasses ?? [];
  const setClassFee = (name: string, amount: number) =>
    set("classFees", { ...form.classFees, [name]: amount });
  const setMatrix = (name: string, phaseId: string, amount: number) =>
    set("classPhaseFees", {
      ...form.classPhaseFees,
      [name]: { ...form.classPhaseFees?.[name], [phaseId]: amount },
    });
  return (
    <>
      <div className="form-section-intro">
        <p className="eyebrow">Etapa 5 de 7</p>
        <h2>Cobrança</h2>
        <p>Defina valores somente depois de cadastrar turmas e fases.</p>
      </div>
      <div className="binary-options billing-type">
        <button
          type="button"
          className={!paid ? "active" : ""}
          onClick={() => set("costType", "free")}
        >
          Gratuita
        </button>
        <button
          type="button"
          className={paid ? "active" : ""}
          onClick={() => set("costType", "paid")}
        >
          Paga
        </button>
      </div>
      {!paid ? (
        <InlineMessage type="success" title="Confirmação automática">
          Inscrições elegíveis serão confirmadas imediatamente.
        </InlineMessage>
      ) : (
        <>
          <div className="billing-options">
            <Toggle
              title="Cobrar dos estudantes"
              description="A cobrança aparece durante a inscrição e depende da quitação para confirmar a participação. Em equipe, o valor é único para o grupo."
              checked={form.studentFeeEnabled === true}
              onChange={(value) => set("studentFeeEnabled", value)}
            />
            <Toggle
              title="Valor diferente por fase"
              description="Cada fase gera sua própria pendência."
              checked={form.feeVariationByPhase === true}
              onChange={(value) => set("feeVariationByPhase", value)}
            />
            <Toggle
              title="Valor diferente por turma"
              description="Permite cobrança específica por turma."
              checked={form.feeVariationByClass === true}
              onChange={(value) => set("feeVariationByClass", value)}
            />
          </div>
          {form.studentFeeEnabled && !form.feeVariationByPhase && !form.feeVariationByClass ? (
            <Field label="Valor por estudante">
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.cost}
                onChange={(e) => set("cost", Number(e.target.value))}
              />
            </Field>
          ) : null}
          {form.studentFeeEnabled && form.feeVariationByPhase && !form.feeVariationByClass ? (
            <InlineMessage title="Valores nas fases">
              Informe a taxa na seção “Provas e fases”.
            </InlineMessage>
          ) : null}
          {form.studentFeeEnabled && form.feeVariationByClass && !form.feeVariationByPhase ? (
            <div className="fee-class-list">
              <p>Valores por turma elegível</p>
              {(selected.length ? [...selected].sort(compareClassNames) : classes).map((name) => (
                <Field key={name} label={formatClassName(name)}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.classFees?.[name] ?? 0}
                    onChange={(e) => setClassFee(name, Number(e.target.value))}
                  />
                </Field>
              ))}
            </div>
          ) : null}
          {form.studentFeeEnabled && form.feeVariationByClass && form.feeVariationByPhase ? (
            <div className="fee-matrix">
              <p>Valores por turma e fase</p>
              {selected.length ? (
                [...selected].sort(compareClassNames).map((name) => (
                  <section key={name}>
                    <strong>{formatClassName(name)}</strong>
                    <div>
                      {form.phases.map((phase) => (
                        <Field key={phase.id} label={phase.title}>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={form.classPhaseFees?.[name]?.[phase.id] ?? 0}
                            onChange={(e) =>
                              setMatrix(name, phase.id, Number(e.target.value))
                            }
                          />
                        </Field>
                      ))}
                    </div>
                  </section>
                ))
              ) : (
                <InlineMessage title="Selecione as turmas">
                  Escolha as turmas elegíveis antes de preencher a matriz.
                </InlineMessage>
              )}
            </div>
          ) : null}
          <Toggle
            title="Taxa paga pelo colégio"
            description="Registra o custo institucional nos relatórios."
            checked={form.schoolFeeEnabled === true}
            onChange={(value) => set("schoolFeeEnabled", value)}
          />
          {form.schoolFeeEnabled ? (
            <Field label="Valor assumido pelo colégio">
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.schoolFeeAmount ?? 0}
                onChange={(e) => set("schoolFeeAmount", Number(e.target.value))}
              />
            </Field>
          ) : null}
          {form.studentFeeEnabled ? <InlineMessage title="Aprovação condicionada">
            Ao registrar a parcela exigida como paga, a inscrição é confirmada
            automaticamente. Em equipe, uma confirmação quita a cobrança de todos os integrantes.
          </InlineMessage> : <InlineMessage title="Custo institucional">
            Sem cobrança ao estudante, as inscrições são confirmadas automaticamente. A taxa do colégio aparece apenas no relatório fiscal.
          </InlineMessage>}
        </>
      )}
    </>
  );
}

function AudienceAndFormat({
  form,
  set,
  classes,
  toggleClass,
}: {
  form: Olympiad;
  set: Setter;
  classes: string[];
  toggleClass: (name: string) => void;
}) {
  const team = form.format === "Equipe";
  return (
    <>
      <div className="form-section-intro">
        <p className="eyebrow">Etapa 2 de 7</p>
        <h2>Participação e turmas</h2>
        <p>Defina primeiro quem participa. Essas turmas serão usadas nas fases e cobranças.</p>
      </div>
      <Field
        label="Turmas participantes"
        hint="Selecione todas as turmas elegíveis para esta olimpíada."
      >
        <div className="choice-chips">
          {classes.map((name) => {
            const selected = form.eligibleClasses?.includes(name) === true;
            return (
              <button
                type="button"
                key={name}
                className={selected ? "active" : ""}
                aria-pressed={selected}
                onClick={() => toggleClass(name)}
              >
                {selected ? <Check size={14} /> : null}
                {formatClassName(name)}
              </button>
            );
          })}
        </div>
      </Field>
      <div className="subsection-heading bordered">
        <strong>Formato de participação</strong>
        <span>Aplicado a todas as turmas selecionadas</span>
      </div>
      <Field label="Formato">
        <select
          value={form.format}
          onChange={(e) => set("format", e.target.value as Olympiad["format"])}
        >
          <option>Solo</option>
          <option>Equipe</option>
        </select>
      </Field>
      {team ? (
        <>
          <Toggle
            title="Tamanho variável"
            description="Permite equipes entre um mínimo e máximo."
            checked={form.variableTeamSize === true}
            onChange={(value) => set("variableTeamSize", value)}
          />
          <div className="form-grid two">
            <Field
              label={
                form.variableTeamSize ? "Mínimo" : "Integrantes por equipe"
              }
            >
              <input
                type="number"
                min="1"
                max="10"
                value={form.teamMin ?? 2}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  set("teamMin", value);
                  if (!form.variableTeamSize) set("teamMax", value);
                }}
              />
            </Field>
            {form.variableTeamSize ? (
              <Field label="Máximo">
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={form.teamMax ?? 4}
                  onChange={(e) => set("teamMax", Number(e.target.value))}
                />
              </Field>
            ) : null}
          </div>
        </>
      ) : (
        <InlineMessage title="Participação individual">
          Cada inscrição corresponde a um estudante.
        </InlineMessage>
      )}
    </>
  );
}
function ContentSettings({ form, set }: { form: Olympiad; set: Setter }) {
  return (
    <>
      <div className="form-section-intro">
        <p className="eyebrow">Etapa 6 de 7</p>
        <h2>Conteúdo e orientações</h2>
        <p>Reúna o que estudantes, famílias e professores precisam saber antes da prova.</p>
      </div>
      <Field label="Conteúdo programático">
        <textarea
          rows={4}
          value={form.content}
          onChange={(e) => set("content", e.target.value)}
        />
      </Field>
      <Field label="Informações adicionais">
        <textarea
          rows={4}
          value={form.additionalInfo}
          onChange={(e) => set("additionalInfo", e.target.value)}
        />
      </Field>
    </>
  );
}
function Review({ form }: { form: Olympiad }) {
  const matrix = form.feeVariationByClass && form.feeVariationByPhase;
  return (
    <>
      <div className="form-section-intro">
        <p className="eyebrow">Etapa 7 de 7</p>
        <h2>Revisar rascunho</h2>
        <p>Confira o fluxo completo antes de salvar. A publicação continua sendo uma ação separada.</p>
      </div>
      <div className="review-card">
        <div>
          <span>Identificação</span>
          <strong>
            {form.acronym || "Sigla pendente"} · {form.name || "Nome pendente"}
          </strong>
        </div>
        <div>
          <span>Inscrições</span>
          <strong>
            Pelo Oficina Sapiens
            {form.continuedEnrollmentEnabled ? " + etapa externa continuada" : ""}
          </strong>
        </div>
        <div>
          <span>Cobrança</span>
          <strong>
            {form.costType === "free"
              ? "Gratuita, confirmação automática"
              : matrix
                ? "Paga por turma e fase"
                : form.feeVariationByPhase
                  ? "Paga por fase"
                  : form.feeVariationByClass
                    ? "Paga por turma"
                    : `Paga, R$ ${form.cost.toFixed(2).replace(".", ",")}`}
          </strong>
        </div>
        <div>
          <span>Formato</span>
          <strong>{form.format}</strong>
        </div>
        <div>
          <span>Fases</span>
          <strong>{form.phases.length} {form.phases.length === 1 ? "fase" : "fases"}</strong>
        </div>
        <div>
          <span>Turmas</span>
          <strong>
            {form.eligibleClasses?.length ? [...form.eligibleClasses].sort(compareClassNames).map(formatClassName).join(", ") : "Nenhuma selecionada"}
          </strong>
        </div>
      </div>
      <div className="review-schedule">
        <h3>Sequência das provas</h3>
        {form.phases.map((phase, index) => (
          <article key={phase.id}>
            <span>{index + 1}</span>
            <div>
              <strong>{phase.title || `Fase ${index + 1}`}</strong>
              <p>
                {phase.datesVaryByClass
                  ? `Datas específicas para ${form.eligibleClasses?.length ?? 0} turma(s)`
                  : formatPhaseDate(phase)}
              </p>
            </div>
          </article>
        ))}
      </div>
      <InlineMessage title="Rascunho antes da publicação">
        Salvar não publica. Revise datas, valores e turmas antes de
        disponibilizar aos estudantes.
      </InlineMessage>
    </>
  );
}
function Toggle({
  title,
  description,
  checked,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="setting-toggle">
      <div>
        <strong>{title}</strong>
        <p>{description}</p>
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  );
}
